import { ChangeEvent, FC, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSnackbar } from 'notistack';

import { Grid, Paper, TablePaginationProps } from '@mui/material';
import { ArrowOutward as ArrowOutwardIcon } from '@mui/icons-material';

import { Table } from '../../components/table/table';
import { HeadCell, Row, RowCell } from '../../components/table/types';

import {
  CreateTransitAddressWithdrawDTO,
  useCreateTransitAddressWithdrawMutation,
  useGetTransitAddressByIdQuery,
  useLazyGetTransitAddressesWithdrawalQuery,
} from '../../api';
import { useAppActions } from '../../utils/hooks';
import { layoutAction } from '../../store/sideBarStore/layoutStore';
import { useParams } from 'react-router-dom';
import { CreateWithdrawalModal } from './components/CreateWithdrawalModal';
import { formatAmount, UTCToDateTime } from '../../utils';
import { TablePagination } from '../../components';
import { permissionsCheck } from '../../utils/permission';

export const TransitWithdrawals: FC = () => {
  const { t } = useTranslation();
  const { enqueueSnackbar } = useSnackbar();
  const { address_id } = useParams<'address_id'>();
  const hasPermissionToEditPayout = permissionsCheck(['WRITE_APPLICATIONS']);

  // api
  const [
    getWithdrawals,
    { data: withdrawals, isLoading: isWithdrawalsLoading, isFetching: isWithdrawalsFetching },
  ] = useLazyGetTransitAddressesWithdrawalQuery();
  const [sendPayout, { isLoading: isCreateAddressLoading }] =
    useCreateTransitAddressWithdrawMutation();
  const { data: address } = useGetTransitAddressByIdQuery({
    addressId: address_id ?? '',
  });

  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(20);
  const handleChangePage: TablePaginationProps['onPageChange'] = (e, newPage) => {
    setPage(newPage);
  };
  const handleChangeRowsPerPage: TablePaginationProps['onRowsPerPageChange'] = (e) => {
    setPage(0);
    setRowsPerPage(parseInt(e.target.value, 10));
  };
  const isLoading = isWithdrawalsLoading || isCreateAddressLoading || isWithdrawalsFetching;

  const { setSpeedDialButtonComponent, removeSpeedDialButtonContent } = useAppActions(layoutAction);
  useEffect(() => {
    hasPermissionToEditPayout &&
      setSpeedDialButtonComponent([{ icon: <ArrowOutwardIcon />, onClick: openCreateModal }]);

    return () => {
      removeSpeedDialButtonContent();
    };
  }, []);

  const { setHeaderTitle, removeHeaderTitle } = useAppActions(layoutAction);
  useEffect(() => {
    if (!address) return;
    setHeaderTitle(`${address?.address.coin.name} (${address?.address.blockchain_address})`);

    return () => {
      removeHeaderTitle();
    };
  }, [address]);

  const [createModalShown, setCreateModalShown] = useState(false);
  const openCreateModal = () => {
    setCreateModalShown(true);
  };
  const closeCreateModal = () => {
    setCreateModalShown(false);
    setFields(null);
  };
  const requiredFields = [
    'address',
    'amount',
    'password',
  ] as (keyof CreateTransitAddressWithdrawDTO['body'])[];
  const checkSubmitDisabling = () => {
    if (!fields) return true;

    for (const field of requiredFields) {
      if (fields[field] === undefined || fields[field] === '') {
        return true;
      }
    }

    return false;
  };
  const [fields, setFields] = useState<CreateTransitAddressWithdrawDTO['body'] | null>(null);
  const handleFields = (e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const name = e.target.name as keyof CreateTransitAddressWithdrawDTO['body'];
    const value = e.target.value;

    // @ts-ignore
    setFields((prevState) => {
      if (!prevState) {
        return {
          [name]: value,
        };
      }

      return {
        ...prevState,
        [name]: value,
      };
    });
  };

  const createTransitAddress = async () => {
    if (!fields || !address_id) return;

    const args = {
      coin_id: Number(address_id),
      body: {
        password: fields.password,
        amount: fields.amount,
        address: fields.address,
      },
    };

    try {
      await sendPayout(args).unwrap();
      closeCreateModal();
      enqueueSnackbar(t('ui.snackbar.success'), { variant: 'success' });
    } catch (e) {
      // @ts-ignore
      enqueueSnackbar(e?.data?.message ?? t('ui.snackbar.error'), { variant: 'error' });
    }
  };

  useEffect(() => {
    const args = {
      addressId: address_id ?? '',
      params: {
        page: page + 1,
        page_size: rowsPerPage,
      },
    };

    getWithdrawals(args);
  }, [page, rowsPerPage]);

  const headCells: HeadCell[] = [
    { id: '1', label: t('transit-addresses.withdraw.head-cells.created-at') },
    { id: '2', label: t('transit-addresses.withdraw.head-cells.status') },
    { id: '3', label: t('transit-addresses.withdraw.head-cells.amount') },
    { id: '4', label: t('transit-addresses.withdraw.head-cells.user-name') },
    { id: '5', label: t('transit-addresses.withdraw.head-cells.hash') },
  ];

  const rows: Row[] | undefined = withdrawals?.withdrawals.map((it) => {
    const { created_at, status, amount, hash, user_info } = it;

    const cells: RowCell[] = [
      { name: 'created_at', value: UTCToDateTime(created_at) },
      { name: 'status', value: status },
      { name: 'amount', value: formatAmount(amount) },
      { name: 'user_name', value: `${user_info?.first_name} ${user_info?.last_name}` },
      { name: 'hash', value: hash },
    ];

    return { cells };
  });

  return (
    <>
      <Grid size={{ xs: 12 }}>
        <Paper sx={{ p: 2, display: 'flex', flexDirection: 'column' }}>
          <Table headCells={headCells} rows={rows} />
          <TablePagination
            page={page}
            rowsPerPage={rowsPerPage}
            total={withdrawals?.total ?? -1}
            totalPages={withdrawals?.total_pages ?? 0}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            isLoading={isLoading}
          />
        </Paper>
      </Grid>

      <CreateWithdrawalModal
        open={createModalShown}
        onClose={closeCreateModal}
        onSuccess={createTransitAddress}
        onCancel={closeCreateModal}
        isLoading={isLoading}
        fields={fields}
        fieldsHandler={handleFields}
        submitDisabling={checkSubmitDisabling()}
      />
    </>
  );
};
